/* =============================================================
   PURE UTILITY HELPERS
   (stateless helpers used for shaping / traversing report trees)
   ============================================================= */

// Recursively assigns a stable, unique id to each node based on its path + index
export const assignIds = (nodes, parentPath = "") =>
  nodes.map((node, index) => {
    const id = `${parentPath}/${node.name}-${index}`;
    return {
      ...node,
      id,
      children: assignIds(node.children || [], id),
    };
  });

// Normalizes numeric amounts coming from backend and stores both raw + display values
export const normalizeAmounts = (node) => {
  const rawAmount = Number(node.amount ?? 0);

  return {
    ...node,
    rawAmount,          
    displayAmount: rawAmount, 
    children: (node.children || []).map(normalizeAmounts),
  };
};

// Builds a Map(id → node) for O(1) lookup during hide / restore operations
export const buildNodeMap = (tree) => {
  const map = new Map();
  const dfs = (node) => {
    map.set(node.id, node);
    (node.children || []).forEach(dfs);
  };
  tree.forEach(dfs);
  return map;
};

// Recomputes a parent's display amount by summing its visible children.
//
// `node` must be the ORIGINAL node, so a node whose children were all hidden
// is still recognised as a parent. Passing the already-filtered node instead
// would make it look like a leaf and wrongly keep its full backend value.
export const recomputeDisplayAmount = (node, visibleChildren) => {
  const originalChildren = node.children || [];
  const visible = visibleChildren ?? originalChildren;

  if (originalChildren.length === 0) {
    return node.rawAmount; // genuine leaf keeps its backend value
  }

  // Had children, so the amount is whatever remains visible — including 0
  // when every child was hidden.
  return visible.reduce((sum, c) => sum + (c.displayAmount ?? 0), 0);
};

// Produces a pruned copy of the tree for Excel export,
// removing hidden nodes and recalculating totals only if needed
export const pruneReportForExcel = (node, removedIds) => {
  if (removedIds.has(node.id)) return null;

  const originalChildren = node.children || [];
  const children = originalChildren
    .map((c) => pruneReportForExcel(c, removedIds))
    .filter(Boolean);

  // Mirrors recomputeDisplayAmount: only a node that never had children keeps
  // its backend amount. A parent whose children were all hidden sums to 0
  // rather than falling back to its original total.
  const amount =
    removedIds.size === 0
      ? node.amount               // untouched backend data
      : originalChildren.length === 0
        ? node.amount
        : children.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  return {
    ...node,
    amount,
    children,
  };
};

const cleanCompanySuffix = (name = "") => {
  return name.replace(/\s*\([^)]*\)\s*$/, "").trim();
};

const mergeMonthlyAmounts = (existing = {}, incoming = {}) => {
  const merged = { ...(existing || {}) };

  Object.keys(incoming || {}).forEach((month) => {
    merged[month] =
      Number(merged[month] || 0) + Number(incoming[month] || 0);
  });

  return merged;
};

export const condenseReportLineItems = (nodes) => {
  const condenseNode = (node) => {
    const children = (node.children || []).map(condenseNode);

    const hasChildren = children.length > 0;

    const allChildrenAreLeaves =
      hasChildren &&
      children.every(
        (child) => !child.children || child.children.length === 0
      );

    // If this section is NOT a pure leaf group, do not group its leaf rows.
    // This keeps standalone rows visible.
    if (!allChildrenAreLeaves) {
      return {
        ...node,
        children,
        defaultCollapsedWhenCondensed: false,
      };
    }

    // Only here: all children are leaf rows, so combine them into one hidden child
    const condensedChild = children.reduce((acc, child) => {
      if (!acc) {
        return {
          ...child,
          name: cleanCompanySuffix(node.name),
          amount: Number(child.amount || 0),
          rawAmount: Number(child.rawAmount ?? child.amount ?? 0),
          displayAmount: Number(child.displayAmount ?? child.amount ?? 0),
          months: child.months ? { ...child.months } : child.months,
          monthsAmount: child.monthsAmount ? { ...child.monthsAmount } : child.monthsAmount,
          amounts: child.amounts ? { ...child.amounts } : child.amounts,
          debit: Number(child.debit || 0),
          credit: Number(child.credit || 0),
          children: [],
        };
      }

      return {
        ...acc,
        amount: Number(acc.amount || 0) + Number(child.amount || 0),
        rawAmount:
          Number(acc.rawAmount || 0) +
          Number(child.rawAmount ?? child.amount ?? 0),
        displayAmount:
          Number(acc.displayAmount || 0) +
          Number(child.displayAmount ?? child.amount ?? 0),
        months: mergeMonthlyAmounts(acc.months, child.months),
        monthsAmount: mergeMonthlyAmounts(acc.monthsAmount, child.monthsAmount),
        amounts: mergeMonthlyAmounts(acc.amounts, child.amounts),
        debit: Number(acc.debit || 0) + Number(child.debit || 0),
        credit: Number(acc.credit || 0) + Number(child.credit || 0),
      };
    }, null);

    return {
      ...node,

      amount: condensedChild.amount,
      rawAmount: condensedChild.rawAmount,
      displayAmount: condensedChild.displayAmount,
      months: condensedChild.months,
      monthsAmount: condensedChild.monthsAmount,
      amounts: condensedChild.amounts,
      debit: condensedChild.debit,
      credit: condensedChild.credit,

      children,
      defaultCollapsedWhenCondensed: true,
    };
  };

  return nodes.map(condenseNode);
};